import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatCents, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select } from "@/components/ui/Primitives";
import { FileText, Plus, ArrowRight, FileSignature, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function Leases() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(params.get("new") === "1");
  const [saving, setSaving] = useState(false);
  const [paywall, setPaywall] = useState(false);
  // Attach a photo of the signed paper lease from the phone's camera.
  const [leasePhoto, setLeasePhoto] = useState(""); // data URL
  const [form, setForm] = useState({
    unit_id: "", tenant_email: "", start_date: "", end_date: "",
    monthly_rent: "", deposit: ""
  });

  const isLandlord = user?.role === "landlord";

  const load = async () => {
    setLoading(true);
    try {
      const calls = [api.get("/leases")];
      if (isLandlord) calls.push(api.get("/units"));
      const res = await Promise.all(calls);
      // Some list endpoints return a plain array, others an envelope
      // `{items: [...], notice: "..."}` when a background service
      // isn't ready yet. Accept both — the whole page crashed with
      // "units.map is not a function" when the envelope shape leaked in.
      const asArray = (payload) =>
        Array.isArray(payload) ? payload :
        Array.isArray(payload?.items) ? payload.items :
        Array.isArray(payload?.data)  ? payload.data  : [];
      setItems(asArray(res[0].data));
      if (isLandlord) setUnits(asArray(res[1].data));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setPaywall(false);
    try {
      await api.post("/leases", {
        unit_id: form.unit_id,
        tenant_email: form.tenant_email.trim(),
        start_date: form.start_date,
        end_date: form.end_date,
        monthly_rent: Math.round(Number(form.monthly_rent) * 100),
        deposit: form.deposit ? Math.round(Number(form.deposit) * 100) : 0,
        photos: leasePhoto ? [leasePhoto] : undefined,
      });
      toast.success("Lease created");
      setOpen(false);
      setForm({ unit_id: "", tenant_email: "", start_date: "", end_date: "", monthly_rent: "", deposit: "" });
      setLeasePhoto("");
      load();
    } catch (err) {
      // Smart-feature paywall — swap the scary red toast for an inline
      // upgrade panel inside the modal so the landlord sees exactly what
      // to do next.
      if (err?.response?.status === 402) {
        setPaywall(true);
      } else {
        toast.error(err?.response?.data?.detail || "Could not create lease");
      }
    } finally { setSaving(false); }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title={isLandlord ? "Leases" : "My lease"}
        subtitle={isLandlord ? "Tenancy agreements across your portfolio." : "Your current rental agreement."}
        actions={isLandlord && (
          <Button onClick={() => setOpen(true)} data-testid="new-lease-button"><Plus className="w-4 h-4"/> New lease</Button>
        )}
      />

      {items.length === 0 ? (
        <EmptyState
          icon={FileText}
          title={isLandlord ? "No leases yet" : "No active lease"}
          description={isLandlord ? "Create a lease to formalize a rental agreement." : "You don't have an active lease yet."}
          action={null}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="leases-grid">
          {items.map((l) => {
            const status = (l.status || "draft").toLowerCase();
            const statusTone =
              status === "signed" || status === "active" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
              status === "expiring" ? "bg-amber-50 text-amber-800 border-amber-200" :
              status === "expired"  ? "bg-rose-50 text-rose-800 border-rose-200" :
              "bg-slate-100 text-slate-700 border-slate-200";
            const initial = (l.tenant_email || "T").trim().charAt(0).toUpperCase();
            return (
              <div key={l.lease_id}
                data-testid={`lease-card-${l.lease_id}`}
                className="group rounded-2xl bg-white border border-slate-200 hover:border-emerald-500 hover:shadow-lg transition-all overflow-hidden">
                {/* Header */}
                <div className="p-4 flex items-start gap-3 border-b border-slate-100">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white font-bold text-sm flex items-center justify-center flex-none shadow-sm">
                    {initial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate text-sm">{l.tenant_email}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Unit · {l.unit_id?.slice(-6) || "—"}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${statusTone}`}>{status}</span>
                </div>

                {/* Body */}
                <div className="p-4 space-y-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Monthly rent</span>
                    <span className="font-display text-lg font-extrabold text-slate-900">{formatCents(l.monthly_rent)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span>{formatDate(l.start_date)}</span>
                    <ArrowRight className="w-3 h-3 text-slate-400" />
                    <span>{formatDate(l.end_date)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-2.5 flex items-center justify-between gap-2">
                  <Link to={`/leases/${l.lease_id}`}
                    data-testid={`open-lease-${l.lease_id}`}
                    className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 hover:text-emerald-900">
                    Open <ArrowRight className="w-3 h-3" />
                  </Link>
                  <div className="flex items-center gap-2">
                    {isLandlord && (
                      <button
                        type="button"
                        onClick={() => {
                          try {
                            sessionStorage.setItem("crc_smart_retention_lease", JSON.stringify({
                              lease_id: l.lease_id,
                              monthly_rent_cad: Math.round((l.monthly_rent || 0) / 100),
                              tenant_email: l.tenant_email || "",
                              end_date: l.end_date || "",
                            }));
                          } catch { /* sessionStorage disabled */ }
                          window.location.href = "/smart?tab=retention";
                        }}
                        data-testid={`retention-risk-${l.lease_id}`}
                        className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-600 hover:text-emerald-700">
                        <TrendingUp className="w-3 h-3" /> Risk
                      </button>
                    )}
                    <Link to={`/leases/${l.lease_id}/envelopes`}
                      data-testid={`e-sign-lease-${l.lease_id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700">
                      <FileSignature className="w-3 h-3" /> E-sign
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => { setOpen(false); setPaywall(false); }} title="Create a new lease" testid="new-lease-modal">
        {paywall ? (
          <div className="text-center py-4" data-testid="lease-paywall">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"/></svg>
            </div>
            <h4 className="mt-3 font-display text-lg font-bold text-slate-900">Lease creation is a paid smart feature</h4>
            <p className="mt-1.5 text-sm text-slate-600 max-w-sm mx-auto">
              Every landlord plan unlocks unlimited lease drafts, N4/L1 notice generation,
              copilot replies, virtual staging, and MLS imports. Paid plans from CA$35/month — no card.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <Link
                to="/billing"
                onClick={() => { setOpen(false); setPaywall(false); }}
                data-testid="lease-paywall-upgrade"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm"
              >
                View plans
              </Link>
              <Button type="button" variant="ghost" onClick={() => setPaywall(false)} data-testid="lease-paywall-back">
                Back to form
              </Button>
            </div>
          </div>
        ) : (
        <form onSubmit={submit} className="space-y-4">
          <Select label="Unit" required value={form.unit_id} onChange={(e)=>setForm({...form, unit_id:e.target.value})} testid="lease-unit-select">
            <option value="">Select a unit</option>
            {units.map((u) => (
              <option key={u.unit_id} value={u.unit_id}>{`${u.label} · ${formatCents(u.rent_amount)}/mo${u.tenant_id ? " (occupied)" : ""}`}</option>
            ))}
          </Select>
          <Input label="Tenant email" type="email" required placeholder="tenant@example.com" value={form.tenant_email} onChange={(e)=>setForm({...form, tenant_email:e.target.value})} testid="lease-tenant-email-input"/>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Start date" type="date" required value={form.start_date} onChange={(e)=>setForm({...form, start_date:e.target.value})} testid="lease-start-input"/>
            <Input label="End date" type="date" required value={form.end_date} onChange={(e)=>setForm({...form, end_date:e.target.value})} testid="lease-end-input"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Monthly rent (CAD)" type="number" step="0.01" min="0" required value={form.monthly_rent} onChange={(e)=>setForm({...form, monthly_rent:e.target.value})} testid="lease-rent-input"/>
            <Input label="Deposit (CAD)" type="number" step="0.01" min="0" value={form.deposit} onChange={(e)=>setForm({...form, deposit:e.target.value})} testid="lease-deposit-input"/>
          </div>
          {/* Mobile-friendly camera capture — attach a photo of the
              signed paper lease. `capture="environment"` opens the rear
              camera on phones; browsers without support fall back to a
              standard file picker. Encoded as a data URL and posted
              alongside the lease. */}
          <div>
            <label className="block text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
              Photo of signed lease (optional)
            </label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              data-testid="lease-photo-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) { setLeasePhoto(""); return; }
                if (f.size > 6 * 1024 * 1024) {
                  toast.error("Photo is too large — keep it under 6 MB.");
                  e.target.value = "";
                  return;
                }
                const reader = new FileReader();
                reader.onload = () => setLeasePhoto(String(reader.result || ""));
                reader.onerror = () => toast.error("Couldn't read that image.");
                reader.readAsDataURL(f);
              }}
              className="block w-full text-xs text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-emerald-50 file:text-emerald-700 file:font-bold hover:file:bg-emerald-100"
            />
            {leasePhoto && (
              <div className="mt-2 relative inline-block" data-testid="lease-photo-preview">
                <img src={leasePhoto} alt="Signed lease preview" className="w-24 h-24 object-cover rounded-md border border-slate-200"/>
                <button
                  type="button"
                  onClick={() => setLeasePhoto("")}
                  data-testid="lease-photo-remove"
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-300 shadow text-slate-500 hover:text-rose-600 text-xs font-bold leading-none"
                >×</button>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="lease-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="lease-create-submit">{saving ? "Creating…" : "Create lease"}</Button>
          </div>
        </form>
        )}
      </Modal>
    </div>
  );
}
